import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { Billing, PopupData, RequestError, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { replaceAll } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class BillingPopupAddonPurchaseComponent extends PopupInnerModalComponent<PopupData.Billing.AddonPurchase> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const origin = replaceAll(this.model.id, '-', '_');
		this.analyticsService.event(TrackingEvent.ADDON_PURCHASE_POPUP_OPENED, { origin });
	}

	get model(): Billing.Addon {
		return this.popupData.model;
	}

	get buttonText() {
		return `Enable ${this.model.name}`;
	}

	@action
	enable() {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;
		this.billingService
			.addAddon(this.model.id)
			.then(
				() => {
					this.infolayerService.ok('Addon purchased!');
					this.close();
				},
				(errorData: RequestError) => {
					this.requestService.handleGenericThrownError(errorData);
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			});
	}

	<template>
		<div class="content">
			<div class="header blue-on-transparent">
				<CommonUiIconComponent @name={{this.model.icon}} @size="lg" />
			</div>

			<h3>Purchase {{this.model.name}}</h3>

			<p>
				<strong>You are about to purchase {{this.model.name}} Add-on.</strong>

				<br />

				This will add
				<strong>
					{{this.model.prices.current.priceFormatted}}
					to your
					{{this.billingService.finalPlan.billingCycleTextLong}}
					bill,
				</strong>
				prorated until your next billing date.
			</p>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text={{this.buttonText}}
						@onTrigger={{this.enable}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
